import { useState } from 'react';
import type { RegionalSummaryItem } from '../rfpTypes';
import { marginColor } from '../rfpUtils';

interface RegionalSummaryProps {
    summary: RegionalSummaryItem[];
}

const fmtUsd = (v: number) =>
    v.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const fmtBbl = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 0 });

export default function RegionalSummary({ summary }: RegionalSummaryProps) {
    const [collapsed, setCollapsed] = useState(false);

    if (!summary.length) return null;

    return (
        <div
            style={{
                marginBottom: 16,
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                overflow: 'hidden',
            }}
        >
            <button
                onClick={() => setCollapsed((c) => !c)}
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: '#dbeafe',
                    border: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 13,
                    color: '#1e3a5f',
                }}
            >
                <span>Regional Summary ({summary.length} region{summary.length !== 1 ? 's' : ''})</span>
                <span>{collapsed ? '▶' : '▼'}</span>
            </button>

            {!collapsed && (
                <div style={{ overflowX: 'auto', padding: '8px 0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#eff6ff' }}>
                                <Th>Region</Th>
                                <Th right>Total BBL/mo</Th>
                                <Th right>Avg Purchase Price</Th>
                                <Th right>Avg Sale Price</Th>
                                <Th right>Avg Trucking Rate</Th>
                                <Th right>Avg Margin</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.map((r, i) => (
                                <tr
                                    key={i}
                                    style={{
                                        borderBottom: '1px solid #e5e7eb',
                                        background: i % 2 === 0 ? '#fff' : '#f9fafb',
                                    }}
                                >
                                    <Td>
                                        <strong>{r.region}</strong>
                                    </Td>
                                    <Td right>{fmtBbl(r.total_monthly_bbl)}</Td>
                                    <Td right>{fmtUsd(r.avg_purchase_price)}</Td>
                                    <Td right>{fmtUsd(r.avg_sale_price)}</Td>
                                    <Td right>{fmtUsd(r.avg_trucking_rate)}</Td>
                                    <td
                                        style={{
                                            padding: '3px 8px',
                                            textAlign: 'right',
                                            fontWeight: 700,
                                            color: marginColor(r.avg_margin),
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {fmtUsd(r.avg_margin)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
    return (
        <th
            style={{
                padding: '5px 8px',
                textAlign: right ? 'right' : 'left',
                fontWeight: 700,
                color: '#1e3a5f',
                whiteSpace: 'nowrap',
                borderBottom: '2px solid #93c5fd',
            }}
        >
            {children}
        </th>
    );
}

function Td({ children, right }: { children: React.ReactNode; right?: boolean }) {
    return (
        <td style={{ padding: '3px 8px', textAlign: right ? 'right' : 'left', color: '#374151', whiteSpace: 'nowrap' }}>
            {children}
        </td>
    );
}
